import {
  ComboboxPopover,
  type Option,
} from '@/components/ui/combobox/combobox-popover';
import type { ResellType, Trade } from '@/models/types';
import { useMemo, useState } from 'react';

import { ResellTypeBadge } from '@/components/deal/trade/inputs/resell-type-badge';
import { pdkAxios } from '@/lib/pdk-axios';
import { tradesStore } from '@/stores/trades-store';
import { useMutation } from '@tanstack/react-query';

// down_payment, full_amount
export const RESALE_TYPE_OPTIONS_MAP: Option<ResellType>[] = [
  {
    label: 'None',
    value: 'none',
    // icon: <Minus />,
    hotkey: '1',
    abbreviation: 'none',
  },
  {
    label: 'Retail',
    value: 'retail',
    // icon: <BanknoteArrowDown />,
    hotkey: '2',
    abbreviation: 'down',
  },
  {
    label: 'Wholesale',
    value: 'wholesale',
    // icon: <PiggyBank />,
    hotkey: '3',
    abbreviation: 'whole',
  },
];

type ResellTypeSelectorCombobox = {
  tradeId: Trade['id'];
  value: ResellType;
  setValue: (value: ResellType) => void;
};

export function ResellTypeSelectorCombobox({
  tradeId,
  value,
  setValue,
}: ResellTypeSelectorCombobox) {
  const [open, setOpen] = useState(false);

  const currentOption = useMemo(() => {
    return RESALE_TYPE_OPTIONS_MAP.find((option) => option.value === value);
  }, [value]);

  const updateResellTypeMutation = useMutation({
    onMutate: async ({ resellType }: { resellType: ResellType }) => {
      tradesStore.updateTrade(tradeId, {
        resell_type: resellType,
      });
    },
    mutationFn: async ({ resellType }: { resellType: ResellType }) => {
      const response = await pdkAxios.post(
        `/trades/${tradeId}/resell_type`,
        // /api/trades/:trade_id/resell_type
        {
          resell_type: resellType,
        },
      );

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error updating resell type', error, variables);
      // TODO: handle error by adding the original cdkId back to the store
    },
  });

  const handleResellTypeChange = (newResellType: ResellType) => {
    updateResellTypeMutation.mutate({ resellType: newResellType });

    setValue(newResellType);
  };

  return (
    <ComboboxPopover
      open={open}
      onOpenChange={setOpen}
      endAdornment={null}
      placeholder="Set resell type..."
      onChange={handleResellTypeChange}
      emptyMessage="No resell type found"
      currentOption={currentOption}
      options={RESALE_TYPE_OPTIONS_MAP}
      // side={side}
      align="start"
      onKeyDown={(e) => {
        const keyPressed = e.key;

        const option = RESALE_TYPE_OPTIONS_MAP.find(
          (option) => option.hotkey === keyPressed,
        );

        if (option) {
          handleResellTypeChange(option.value);
          e.preventDefault();
          e.stopPropagation();
          setOpen(false);
        }
      }}
      stayOpenAfterSelection={false}
    >
      <div
      // DEV NOTE: without this div, combobox-popover won't work
      // that is, clicks will not open the combobox
      // I think the reason is children node needs to be able to receive a ref
      // in order to detect a click event, which makes it open
      // thus so this div is a workaround
      >
        <ResellTypeBadge value={value} />
      </div>
    </ComboboxPopover>
  );
}
